import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded, Link } from '@mui/icons-material';
import { Button, SxProps, Theme, Typography } from '@mui/material';
import Constants from 'style/constants';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import Divider from 'shared/components/divider/divider';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { getUnitLocationKey } from 'shared/utils/IS32-utils';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { SystemStatus } from 'features/systems/domain/models/system-status';
import { useIS32ContextProvider } from 'features/systems/IS32/context/IS32-provider';
import { IS32AccordionType } from 'features/systems/IS32/domain/models/IS32-accordion-type';
import { useIS32DetailsContextProvider } from 'features/systems/IS32/providers/IS32-details.provider';
import IS32ConfigurationAccordion from 'features/systems/IS32/views/configuration-components/IS32-configuration-accordion.component';
import styles from 'features/systems/IS32/views/configuration-components/IS32-location-configuration.module.scss';

const IS32LocationConfiguration = () => {
  const { t } = useTranslation('IS32');
  const { hasPermission } = usePermissionAuthorization();

  const {
    systemsDetailsHook: { viewingMode, selectedSystem },
  } = useSystemsContextProvider();
  const isSystemOffline = selectedSystem?.status === SystemStatus.offline;
  const { openDeviceDetailsByExternalId } = useIS32ContextProvider();
  const {
    selectedIS32ConfigurationUnit,
    createIS32Device,
    unitLocationHasDevice,
    unitLocationHasLinkedDevice,
    getUnitLocationDeviceBreadCrumbs,
    is32AccordionHook: { locationsExpanded, handleAccordionExpandedChange },
  } = useIS32DetailsContextProvider();

  const textSx: SxProps<Theme> = {
    '&.Mui-disabled': {
      color: Constants.Colors.primary,
      opacity: 0.38,
    },
  };

  const getSubUnitLocations = (locationType: string) => {
    const subUnitLocations = Array.from({ length: 8 }, (_, index) => (
      <div className={styles.row} key={getUnitLocationKey(selectedIS32ConfigurationUnit?.address, locationType, index + 1)}>
        <div className={styles.nameOrganisationUnitContainer}>
          <Typography
            className={`${viewingMode === 'viewing' && unitLocationHasDevice(locationType, index + 1) ? styles.clickable : ''}`}
            variant="subtitle1"
            onClick={() =>
              unitLocationHasDevice(locationType, index + 1) &&
              openDeviceDetailsByExternalId(`${selectedIS32ConfigurationUnit?.address}_${locationType}${index + 1}`)
            }
            sx={{ width: 56 }}
          >
            {t(`locations.${locationType}`, { index: index + 1 })}
          </Typography>
          {unitLocationHasLinkedDevice(locationType, index + 1) && (
            <BreadCrumbTooltip allElementNames={getUnitLocationDeviceBreadCrumbs(locationType, index + 1)}>
              <Link sx={{ fontSize: 18 }} />
            </BreadCrumbTooltip>
          )}
        </div>

        {hasPermission(Permission.CreateDevice) && !unitLocationHasDevice(locationType, index + 1) && (
          <Button
            startIcon={<AddCircleOutlineRounded />}
            sx={textSx}
            variant="text"
            onClick={() => createIS32Device(index + 1, locationType, undefined)}
            onFocus={(event) => event.stopPropagation()}
            disabled={viewingMode === 'editing' || isSystemOffline}
            className={styles.unlinkButton}
          >
            {t('actions.createDevice')}
          </Button>
        )}
      </div>
    ));

    return <>{subUnitLocations}</>;
  };

  const header = () => <Divider className={styles.divider} startText={t('locations.title')} variant="h6" />;

  const details = () => (
    <div className={styles.contentContainer}>
      <div className={styles.roomRow}>
        <Typography
          className={`${viewingMode === 'viewing' ? styles.clickable : ''}`}
          variant="subtitle1"
          onClick={() => unitLocationHasDevice('room') && openDeviceDetailsByExternalId(`${selectedIS32ConfigurationUnit?.address}_room`)}
          sx={{ width: 56 }}
        >
          {t('locations.room')}
        </Typography>
        {unitLocationHasLinkedDevice('room') && (
          <BreadCrumbTooltip allElementNames={getUnitLocationDeviceBreadCrumbs('room')}>
            <Link sx={{ fontSize: 18 }} />
          </BreadCrumbTooltip>
        )}
      </div>

      {getSubUnitLocations('bed')}
      {getSubUnitLocations('toilet')}
    </div>
  );

  return (
    <IS32ConfigurationAccordion
      header={header()}
      details={details()}
      isExpanded={locationsExpanded}
      handleAccordionExpandedChange={(expanded) => handleAccordionExpandedChange(IS32AccordionType.Locations, expanded)}
    />
  );
};

export default IS32LocationConfiguration;
